import React, { useState } from 'react';
import { X, BookOpen } from 'lucide-react';
import { createStory } from '../services/firestore';
import { moderateContent } from '../services/moderation';

export default function AddStoryModal({ isOpen, onClose, user, onSuccess }) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      setError('You must be logged in to post');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Run AI moderation
      const modResult = await moderateContent(title, text, 'bar story');

      if (modResult.decision === 'REJECT') {
        setError(`⚠️ Story not allowed: ${modResult.reason}. Share genuine drunk coding experiences, not spam or marketing.`);
        setLoading(false);
        return;
      }

      await createStory({
        title,
        text,
        author: user.displayName || user.email || 'Anonymous Drunk'
      }, user.uid);

      onSuccess?.();
      onClose();

      // Reset form
      setTitle('');
      setText('');
    } catch (err) {
      setError(err.message || 'Failed to create story');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl p-8 max-w-2xl w-full border border-slate-800 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BookOpen className="text-amber-500" />
            Share Your Bar Story
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-bold text-slate-300 mb-2">Story Title*</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., How I bought a $3000 domain at 3am"
              className="w-full bg-slate-800 text-white px-4 py-3 rounded-lg border border-slate-700 focus:border-amber-500 focus:outline-none"
              required
              maxLength={150}
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-300 mb-2">Your Story*</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Tell us about your drunk coding adventure, failed startup attempt, or regrettable technical decision..."
              className="w-full bg-slate-800 text-white px-4 py-3 rounded-lg border border-slate-700 focus:border-amber-500 focus:outline-none h-48"
              required
              maxLength={2000}
            />
            <div className="text-xs text-slate-500 mt-1">{text.length}/2000</div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-4 rounded-lg transition disabled:opacity-50"
          >
            {loading ? 'Posting...' : 'Share Story 📖'}
          </button>
        </form>
      </div>
    </div>
  );
}
